import { type Logger, TaggedLogger } from '@core/logging/logger.ts'
import { Env } from '../env.ts'

/**
 * Minimum scrollable range (px) a scroller must have before a depth sample is accepted or a
 * baseline is frozen. Below this, "depth" is meaningless as an engagement signal and a single
 * wheel tick could read as 100% (e.g. a transiently 40px-scrollable page during hydration).
 */
const MIN_SCROLL_RANGE_PX = 200

/**
 * Fraction of the viewport (both axes) an element must cover to qualify as the page's main scroll
 * container. Accepts `#app { height: 100vh; overflow-y: auto }` shells and dominant panes while
 * rejecting chat widgets, dropdowns, code blocks, and side rails.
 */
const MIN_VIEWPORT_COVERAGE = 0.5

/**
 * Upper bound on distinct scroll targets sampled in one animation frame. Nested scrolling rarely
 * involves more than 2-3 real containers; the cap bounds rAF work under pathological event storms.
 * Targets dropped by the cap are simply picked up on a later frame (running-max makes drops safe).
 */
const MAX_TARGETS_PER_FRAME = 4

/**
 * How recently a user input gesture (wheel/touch/key/mousedown) must have occurred for a baseline
 * to be frozen, when gesture gating is bound. Programmatic scrolls the browser performs on its own
 * (scroll restoration, SPA clamp-on-shrink, anchor jumps on load) have no nearby input event, so
 * they can never establish a baseline. Generous enough to cover touch-drag → first scroll sample.
 */
const GESTURE_RECENCY_MS = 1500

/**
 * How long a non-static negative eligibility verdict is trusted before the element is re-evaluated
 * on its next scroll. Lets a container that becomes the main scroller after content loads (or a
 * layout change) re-qualify without a navigation.
 */
const ELIGIBILITY_RETRY_MS = 2000

/**
 * Frozen-denominator state for one scroller (the document or an element).
 *
 * The depth denominator is frozen to the scroller's scrollable range at the first *confirmed*
 * sample (see {@link ScrollDepthObserver._resolveBaselineRange}) and never updated until the next
 * navigation. This keeps percentage thresholds reachable on infinite-scroll/growing pages
 * (depth is "% of the initially loaded content") and blocks the verified poisoning case where a
 * shrinking document clamps the scroll offset to an arithmetically-perfect 100%.
 */
interface BaselineSlot {
    /** The frozen scrollable range (px), or null while unconfirmed (samples are withheld). */
    range: number | null
    /** The scroller height seen by the previous sample, awaiting stability confirmation. */
    candidateHeight: number | null
}

/** Cached per-element qualification + baseline, invalidated lazily via {@link generation}. */
interface ElementScrollerState extends BaselineSlot {
    eligible: boolean
    /** True when the verdict can never change (form control / editable / SDK-owned DOM). */
    staticVerdict: boolean
    /** Navigation generation the verdict was computed under; stale entries are re-evaluated. */
    generation: number
    /** Epoch ms of the evaluation, for re-trying dynamic negatives after {@link ELIGIBILITY_RETRY_MS}. */
    evaluatedAt: number
}

export class ScrollDepthObserver {
    private static readonly GESTURE_EVENTS = ['wheel', 'touchstart', 'touchmove', 'keydown', 'mousedown'] as const
    private static readonly GESTURE_LISTENER_OPTIONS = { passive: true, capture: true } as const

    public static _init(scrollDepthTrackingEnabled: boolean, logger?: Logger): ScrollDepthObserver {
        logger ??= TaggedLogger._getInstance('ScrollDepthObserver')
        return new ScrollDepthObserver(scrollDepthTrackingEnabled, logger)
    }

    /** Highest computed depth on the current page since the last navigation; starts at 0. */
    private maxDepth: number = 0

    /** Whether the observer is currently subscribed to (driven by) the shared scroll signal. */
    private tracking: boolean = false

    /** True while a sample is scheduled for the next animation frame (scroll-event coalescing). */
    private framePending: boolean = false

    /** Handle for the scheduled animation frame, so it can be cancelled on stop. */
    private frameHandle: number | null = null

    /**
     * Distinct scroll targets seen since the last processed frame (`null` = the document scroller).
     * Drained and cleared by every `_sampleDepth` run and on stop/navigation, so it never holds an
     * element reference for longer than one frame.
     */
    private readonly pendingTargets = new Set<EventTarget | null>()

    /** Frozen-denominator state for the document scroller. */
    private readonly documentBaseline: BaselineSlot = { range: null, candidateHeight: null }

    /**
     * Per-element qualification + baseline cache. WeakMap so elements detached by SPA re-renders
     * are collected together with their entries — no cleanup pass exists or is needed.
     */
    private readonly elementStates = new WeakMap<Element, ElementScrollerState>()

    /** Bumped on navigation; element states from older generations are lazily re-evaluated. */
    private generation: number = 0

    /**
     * Whether baselines additionally require a recent user input gesture (see
     * {@link _bindGestureSignals}). Off by default so bare observers (unit tests, degraded
     * environments) do not withhold forever with no gesture source attached.
     */
    private gestureGatingEnabled: boolean = false

    /** Epoch ms of the most recent user input gesture; 0 = none observed yet. */
    private lastGestureAt: number = 0

    /** Handler and exact document retained so gesture listeners can be detached on teardown. */
    private gestureHandler?: () => void
    private gestureDocument?: Document

    protected constructor(
        private readonly scrollDepthTrackingEnabled: boolean,
        private readonly logger: Logger,
    ) {}

    /**
     * The highest scroll depth percentage computed on the current page since the most recent
     * navigation. Initialized to zero and reset to zero on navigation.
     */
    public get _currentMaxDepth(): number {
        return this.maxDepth
    }

    /**
     * Begins responding to the shared scroll signal. When scroll depth tracking is disabled
     * (no active prompt uses the trigger) this is a no-op: the observer neither subscribes nor
     * computes, and `Current_Page_Max_Depth` remains at zero.
     */
    public _start(): void {
        if (!this.scrollDepthTrackingEnabled) {
            this.logger.verbose('Scroll depth tracking disabled - not subscribing to scroll signal')
            return
        }

        this.tracking = true
    }

    /** Stops responding to scroll signals, detaches gesture listeners and cancels pending work. */
    public _stop(): void {
        this.tracking = false
        this._unbindGestureSignals()
        this._cancelPendingFrame()
        this.pendingTargets.clear()
    }

    /**
     * Reacts to a scroll event from the single shared capture-phase passive listener. The event's
     * target identifies which scroller moved: the Document (or nothing, for legacy no-event
     * callers) routes to the window/document math; an Element routes to the per-container math
     * behind the eligibility heuristic. Rather than reading layout per event, distinct targets are
     * accumulated and the actual computation is coalesced into the next animation frame: at most
     * one layout-reading pass per frame regardless of how many scroll events arrive. No-ops while
     * tracking is disabled or stopped.
     */
    public _onScroll(ev?: Event): void {
        if (!this.scrollDepthTrackingEnabled || !this.tracking) {
            return
        }

        const target = ev?.target ?? null
        if (this.pendingTargets.size < MAX_TARGETS_PER_FRAME || this.pendingTargets.has(target)) {
            this.pendingTargets.add(target)
        }

        this._scheduleSample()
    }

    /**
     * Attaches capture-phase passive listeners for user input gestures (wheel/touch/key/mousedown)
     * and enables gesture gating of baseline freezing: from then on a scroller's denominator can
     * only be frozen within {@link GESTURE_RECENCY_MS} of real user input. This is what stops
     * browser-initiated scrolls — scroll restoration racing lazy content, the clamp-scroll fired
     * when an SPA route teardown shrinks the document, anchor jumps on load — from establishing a
     * (possibly skeleton-sized) baseline: they emit no input events. `isTrusted` cannot make this
     * distinction (UA-dispatched restoration scrolls are trusted), hence dedicated input listeners.
     *
     * Bound from the SDK wiring only when scroll-depth tracking is in use; a bare observer keeps
     * gating off so environments without an input source are not withheld forever.
     */
    public _bindGestureSignals(): void {
        const doc = Env._scope.document
        if (
            this.gestureHandler ||
            typeof doc?.addEventListener !== 'function' ||
            typeof doc.removeEventListener !== 'function'
        ) {
            return
        }

        this.gestureGatingEnabled = true
        this.gestureDocument = doc
        this.gestureHandler = () => this._onUserGesture()

        for (const type of ScrollDepthObserver.GESTURE_EVENTS) {
            doc.addEventListener(type, this.gestureHandler, ScrollDepthObserver.GESTURE_LISTENER_OPTIONS)
        }
    }

    /** Detaches gesture signals from the same document and resets the gate for final teardown. */
    private _unbindGestureSignals(): void {
        const doc = this.gestureDocument
        const handler = this.gestureHandler
        if (doc && handler) {
            for (const type of ScrollDepthObserver.GESTURE_EVENTS) {
                doc.removeEventListener(type, handler, ScrollDepthObserver.GESTURE_LISTENER_OPTIONS)
            }
        }

        this.gestureDocument = undefined
        this.gestureHandler = undefined
        this.gestureGatingEnabled = false
        this.lastGestureAt = 0
    }

    /** Records a user input gesture for baseline gating. */
    public _onUserGesture(): void {
        this.lastGestureAt = Date.now()
    }

    /**
     * Resets `Current_Page_Max_Depth` to zero and re-arms every frozen baseline. Invoked from the
     * Navigation_Signal so each page (including SPA route changes) starts fresh. Any pending
     * sample and its accumulated targets are cancelled so a pre-navigation reading cannot carry
     * into the new page; element states are invalidated lazily via the generation counter.
     */
    public _onPageChanged(): void {
        this._cancelPendingFrame()
        this.pendingTargets.clear()
        this.maxDepth = 0
        this.documentBaseline.range = null
        this.documentBaseline.candidateHeight = null
        this.generation++
    }

    /** Schedules a single scroll-depth sample for the next animation frame (coalescing). */
    private _scheduleSample(): void {
        if (this.framePending) {
            return
        }
        this.framePending = true

        const scope = Env._scope as unknown as {
            requestAnimationFrame?: (cb: () => void) => number
        }

        const run = () => {
            this.framePending = false
            this.frameHandle = null
            this._sampleDepth()
        }

        if (typeof scope.requestAnimationFrame === 'function') {
            this.frameHandle = scope.requestAnimationFrame(run)
        } else {
            // No animation-frame scheduler available (non-browser / degraded env): sample now.
            run()
        }
    }

    /** Cancels a scheduled sample, if any. */
    private _cancelPendingFrame(): void {
        if (this.frameHandle !== null) {
            const scope = Env._scope as unknown as { cancelAnimationFrame?: (handle: number) => void }
            scope.cancelAnimationFrame?.(this.frameHandle)
            this.frameHandle = null
        }
        this.framePending = false
    }

    /**
     * Drains the targets accumulated since the last frame, computes a depth for each, and updates
     * `Current_Page_Max_Depth` to the running max. A single global max across the document and all
     * eligible containers: the consumer is a one-shot `>= threshold` gate, so per-container maxima
     * would add state without ever changing its decision.
     */
    private _sampleDepth(): void {
        let frameMax = 0
        try {
            for (const target of this.pendingTargets) {
                try {
                    const depthPct = this._depthFor(target)
                    if (depthPct > frameMax) {
                        frameMax = depthPct
                    }
                } catch (error) {
                    this.logger.verbose('Scroll depth sample failed', error)
                }
            }
        } finally {
            this.pendingTargets.clear()
        }

        if (frameMax > this.maxDepth) {
            this.maxDepth = frameMax
        }
    }

    /**
     * Routes one scroll target to its depth math. `null` (no event — legacy callers) and the
     * document/root-element targets use the window math; other Elements use the per-container math
     * gated by the eligibility heuristic. Anything else (shadow-root nodes, text nodes) yields 0.
     * The Element check is realm-safe (`nodeType`) because scroll targets and `Env._scope` may
     * come from different globals (tests, sourceless iframes).
     */
    private _depthFor(target: EventTarget | null): number {
        if (target === null) {
            return this._computeDepthPct()
        }

        const scope = Env._scope
        const doc = scope.document
        if (target === (scope as unknown as EventTarget) || target === doc) {
            return this._computeDepthPct()
        }
        if (doc && (target === doc.documentElement || target === doc.scrollingElement)) {
            return this._computeDepthPct()
        }

        if ((target as Node).nodeType !== 1) {
            return 0
        }

        return this._computeElementDepthPct(target as Element)
    }

    /**
     * Computes the current **document** scroll progress percentage as an integer in `[0, 100]`,
     * measured against a frozen per-page baseline:
     *
     * ```
     * root     = document.scrollingElement ?? documentElement   (quirks-mode safe)
     * viewport = documentElement.clientHeight || innerHeight    (excludes classic scrollbars)
     * baseline = frozen at the first confirmed sample: root.scrollHeight - viewport
     * pct      = scrollY >= baseline - 1 ? 100 : clamp(floor(scrollY / baseline * 100), 0, 100)
     * ```
     *
     * A sample is **withheld** (yields `0`, which can never raise the running max) until the
     * baseline is confirmed: the scrollable range must be at least {@link MIN_SCROLL_RANGE_PX},
     * the page height must be stable across two consecutive samples, and — when gesture gating is
     * bound — a user input gesture must be recent. See {@link _resolveBaselineRange} for why.
     * Once frozen, the baseline holds until navigation: on growing pages (infinite scroll, lazy
     * content) depth reads as "% of the initially loaded content" so configured thresholds stay
     * reachable, and on shrinking pages the browser's clamp-to-bottom cannot fabricate a 100%.
     * `Math.floor` matches the `>= threshold` gate ("at least N%"); the 1px tolerance covers
     * fractional `scrollY` never quite reaching the integer range at the true bottom.
     */
    protected _computeDepthPct(): number {
        const scope = Env._scope
        const doc = scope.document
        const documentElement = doc?.documentElement
        const root = doc?.scrollingElement ?? documentElement
        if (!root || !documentElement) {
            return 0
        }
        const viewportHeight = documentElement.clientHeight || scope.innerHeight

        const baselineRange = this._resolveBaselineRange(this.documentBaseline, root.scrollHeight, viewportHeight)
        if (baselineRange === null) {
            return 0
        }

        return this._progressPct(scope.scrollY, baselineRange)
    }

    /**
     * Computes the scroll progress percentage of an inner scroll container, mirroring the document
     * math (`scrollTop` against a frozen `scrollHeight - clientHeight` baseline) behind the
     * {@link _evaluateEligibility} heuristic. Ineligible or unconfirmed containers are withheld
     * (yield `0`) rather than guessed at — a false positive burns the one-shot prompt, a false
     * negative merely delays it.
     */
    protected _computeElementDepthPct(el: Element): number {
        const state = this._stateFor(el)
        if (!state.eligible) {
            return 0
        }

        const baselineRange = this._resolveBaselineRange(state, el.scrollHeight, el.clientHeight)
        if (baselineRange === null) {
            return 0
        }

        return this._progressPct(el.scrollTop, baselineRange)
    }

    /**
     * Returns the scroller's frozen baseline range, confirming (freezing) it first when the
     * current sample qualifies; returns `null` while unconfirmed, which withholds the sample.
     *
     * Freezing requires all of:
     *  1. range >= {@link MIN_SCROLL_RANGE_PX} — a meaningful scrollable range;
     *  2. the scroller's height equals the previous sample's (two-sample stability) — a single
     *     browser-initiated scroll event (restoration, shrink-clamp) can never freeze a baseline,
     *     and mid-hydration heights keep resetting the candidate until layout settles;
     *  3. a recent user gesture, when gesture gating is bound — excludes multi-event programmatic
     *     scrolling over a stable-but-wrong skeleton height.
     */
    private _resolveBaselineRange(slot: BaselineSlot, height: number, viewportHeight: number): number | null {
        if (slot.range !== null) {
            return slot.range
        }

        const range = height - viewportHeight
        if (range < MIN_SCROLL_RANGE_PX) {
            slot.candidateHeight = null
            return null
        }

        if (slot.candidateHeight === height && this._gestureAllowsBaseline()) {
            slot.range = range
            return range
        }

        slot.candidateHeight = height
        return null
    }

    /** Whether baseline freezing is currently permitted by the gesture gate. */
    private _gestureAllowsBaseline(): boolean {
        if (!this.gestureGatingEnabled) {
            return true
        }
        return Date.now() - this.lastGestureAt <= GESTURE_RECENCY_MS
    }

    /**
     * Progress percentage of `position` within `range` as an integer in `[0, 100]`. `Math.floor`
     * keeps "at least N%" semantics; positions within 1px of (or beyond) the range read as 100 —
     * `scrollTop`/`scrollY` are fractional while heights are rounded integers, so the exact bottom
     * can otherwise be unreachable, and positions beyond a frozen baseline (content grew) mean the
     * initially loaded content was fully scrolled through.
     */
    private _progressPct(position: number, range: number): number {
        if (position >= range - 1) {
            return 100
        }
        return this.clamp(Math.floor((position / range) * 100), 0, 100)
    }

    /** Returns the element's cached scroller state, (re-)evaluating eligibility when stale. */
    private _stateFor(el: Element): ElementScrollerState {
        const cached = this.elementStates.get(el)
        if (cached && cached.generation === this.generation) {
            const retryable =
                !cached.eligible && !cached.staticVerdict && Date.now() - cached.evaluatedAt > ELIGIBILITY_RETRY_MS
            if (!retryable) {
                return cached
            }
        }

        const state = this._evaluateEligibility(el)
        this.elementStates.set(el, state)
        return state
    }

    /**
     * Decides whether an element plausibly is the page's **main** scroll container, cheap checks
     * first so the layout-reading calls only run for candidates that pass the free ones:
     *
     *  1. not a form control / editable region / the SDK's own prompt DOM (static verdicts);
     *  2. vertically scrollable by a meaningful range (>= {@link MIN_SCROLL_RANGE_PX}) — note
     *     `scrollHeight > clientHeight` alone is insufficient in general (`overflow: visible`
     *     content also inflates scrollHeight), hence check 3;
     *  3. computed `overflow-y` establishes a scroll container the user can actually scroll
     *     (`auto`/`scroll`/`overlay`) — also filters programmatic `scrollTop` movement of
     *     `overflow: hidden` carousels/tickers;
     *  4. covers >= {@link MIN_VIEWPORT_COVERAGE} of the viewport in **both** axes — the
     *     load-bearing guard that admits app shells and dominant panes while rejecting chat
     *     widgets, dropdowns, code blocks, and side rails.
     *
     * Without this heuristic, any tiny scroller scrolled to its own bottom would set
     * `Current_Page_Max_Depth` to 100 and fire the prompt.
     */
    private _evaluateEligibility(el: Element): ElementScrollerState {
        const base = {
            generation: this.generation,
            evaluatedAt: Date.now(),
            range: null,
            candidateHeight: null,
        }
        const ineligible = (staticVerdict: boolean): ElementScrollerState => ({
            ...base,
            eligible: false,
            staticVerdict,
        })

        const tag = typeof el.tagName === 'string' ? el.tagName.toUpperCase() : ''
        if (tag === 'TEXTAREA' || tag === 'SELECT' || tag === 'INPUT') {
            return ineligible(true)
        }
        if ((el as HTMLElement).isContentEditable) {
            return ineligible(true)
        }
        if (typeof el.closest === 'function' && el.closest('.push-sdk-prompt-container')) {
            return ineligible(true)
        }

        if (el.scrollHeight - el.clientHeight < MIN_SCROLL_RANGE_PX) {
            return ineligible(false)
        }

        const scope = Env._scope
        if (typeof scope.getComputedStyle === 'function') {
            const overflowY = scope.getComputedStyle(el).overflowY
            if (overflowY !== 'auto' && overflowY !== 'scroll' && overflowY !== 'overlay') {
                return ineligible(false)
            }
        }

        const viewportWidth = scope.innerWidth
        const viewportHeight = scope.innerHeight
        if (!viewportWidth || !viewportHeight || typeof el.getBoundingClientRect !== 'function') {
            return ineligible(false)
        }
        const rect = el.getBoundingClientRect()
        if (
            rect.height < viewportHeight * MIN_VIEWPORT_COVERAGE ||
            rect.width < viewportWidth * MIN_VIEWPORT_COVERAGE
        ) {
            return ineligible(false)
        }

        this.logger.verbose('Qualified scroll container for depth tracking')
        return { ...base, eligible: true, staticVerdict: false }
    }

    /** Bounds `value` to the inclusive range `[min, max]`. */
    private clamp(value: number, min: number, max: number): number {
        return Math.min(Math.max(value, min), max)
    }
}
